/**
 * Engine-level multi-timeframe support for the backtest suite.
 *
 * Given a base bar series and one or more raw higher-timeframe (HTF) bar
 * arrays, builds aligned, no-lookahead lookup structures so strategies can
 * consult HTF data from within the per-bar loop without re-deriving it.
 *
 * Pure functions — no React, no engine coupling beyond `normalizeBars`.
 */

import { normalizeBars } from './bars';

/**
 * Resample 1m (or any finer) bars into N-minute OHLCV buckets, anchored to
 * the timestamp of the first bar. Documented fallback path — not used by
 * default (the engine prefers native HTF bars supplied by the caller), but
 * available for callers that only have a base series on hand.
 */
export function resample(bars, minutes) {
  if (!Array.isArray(bars) || !bars.length || !(minutes > 0)) return [];
  const bucketSeconds = minutes * 60;
  const anchor = bars[0].timeSec;
  const out = [];
  let current = null;
  let currentBucket = null;

  for (const bar of bars) {
    if (!bar || !Number.isFinite(bar.timeSec)) continue;
    const bucket = Math.floor((bar.timeSec - anchor) / bucketSeconds);
    if (bucket !== currentBucket) {
      if (current) out.push(current);
      const bucketStart = anchor + bucket * bucketSeconds;
      currentBucket = bucket;
      current = {
        time: bucketStart,
        timeSec: bucketStart,
        timestamp: new Date(bucketStart * 1000).toISOString(),
        open: bar.open,
        high: bar.high,
        low: bar.low,
        close: bar.close,
        volume: Number(bar.volume) || 0,
      };
    } else {
      current.high = Math.max(current.high, bar.high);
      current.low = Math.min(current.low, bar.low);
      current.close = bar.close;
      current.volume += Number(bar.volume) || 0;
    }
  }
  if (current) out.push(current);
  out.forEach((bar, index) => { bar.index = index; });
  return out;
}

function isNormalized(bars) {
  return Array.isArray(bars) && bars.every((bar) => bar && Number.isFinite(bar.timeSec));
}

/**
 * Build aligned, no-lookahead lookup structures for each required timeframe.
 *
 * @param {Array} baseBars - normalized base bar series (time-sorted).
 * @param {Object} timeframeBarsByKey - { [key]: rawBars } raw/normalized HTF bar arrays.
 * @param {Array} requiredTimeframes - strategy's declared [{key, interval, unit}].
 * @returns {Object} { [key]: { bars, idxAt, completedAt, at } }
 */
export function buildTimeframes(baseBars, timeframeBarsByKey, requiredTimeframes) {
  if (!Array.isArray(requiredTimeframes) || requiredTimeframes.length === 0) return {};

  const base = Array.isArray(baseBars) ? baseBars : [];
  const out = {};

  for (const req of requiredTimeframes) {
    const key = req?.key;
    if (!key) continue;
    const raw = timeframeBarsByKey ? timeframeBarsByKey[key] : null;
    const htf = isNormalized(raw) ? raw : normalizeBars(raw, req.unit || 'Minute');

    const n = base.length;
    const m = htf.length;
    const idxAt = new Int32Array(n);
    const completedAt = new Int32Array(m);
    completedAt.fill(-1);

    // Two-pointer merge: idxAt[i] = largest j such that htf[j].timeSec <= base[i].timeSec.
    let j = -1;
    for (let i = 0; i < n; i++) {
      const t = base[i].timeSec;
      while (j + 1 < m && htf[j + 1].timeSec <= t) j++;
      idxAt[i] = j;
    }

    // completedAt[j] = smallest i such that base[i].timeSec >= htf[j].timeSec.
    let i2 = 0;
    for (let jj = 0; jj < m; jj++) {
      const t = htf[jj].timeSec;
      while (i2 < n && base[i2].timeSec < t) i2++;
      completedAt[jj] = i2 < n ? i2 : -1;
    }

    const at = (i) => {
      const idx = idxAt[i];
      return idx >= 0 && idx < m ? htf[idx] : null;
    };

    out[key] = { bars: htf, idxAt, completedAt, at };
  }

  return out;
}

export default buildTimeframes;
